import React from 'react';
import * as Yup from 'yup';
import _ from 'lodash';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { Box, Stack, Typography, TextField, Button, Modal, Card, CardContent, CardHeader, Checkbox, Alert, LinearProgress, IconButton, Tooltip, FormControl, InputLabel, Select, OutlinedInput, MenuItem, ListItemText, ListItemAvatar, Avatar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import defaultImage from './default.png';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const validationSchema = Yup.object({
  email_message: Yup.boolean(),
  sms_message: Yup.boolean(),
  sms_message_any_number: Yup.boolean(),
  message: Yup.string().required('To pole nie może być puste'),
  subject: Yup.string().when('email_message', {
    is: true,
    then: Yup.string().required('To pole nie może być puste')
  }),
  any_numbers: Yup.string().when('sms_message_any_number', {
    is: true,
    then: Yup.string().required('To pole nie może być puste')
  })
});

export default function SendMessagesModal({ rows, sendMessages, setOpen, type }) {
  const { mutate, error, loading, reset } = useMutation(sendMessages);
  const [idList, setIdList] = React.useState([]);

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const handleChange = e => {
    setIdList(e.target.value);
  };

  const handleSubmit = async values => {
    const { error: mutateError, payload } = await mutate(values, type);
    if (!mutateError) {
      formik.resetForm();
    }
  };

  const formik = useFormik({
    initialValues: {
      email_message: false,
      sms_message: false,
      sms_message_any_number: false,
      message: '',
      subject: '',
      any_numbers: ''
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: values => {
      values.id_list = idList;
      handleSubmit(values);
    }
  });

  return (
    <Modal
      open={true}
      onClose={handleClose}
    >
      <Card sx={{ ...style, width: '35vw' }}>
        <CardHeader
          title="Wyślij wiadomość"
          action={

            <Tooltip title="zamknij">
              <IconButton color="primary" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>

          }
          sx={{ textAlign: 'center' }}
        />
        <CardContent>
          <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" justifyContent="left" spacing={4}>
              <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                <Checkbox id="email_message" name="email_message" checked={formik.values.email_message} onChange={formik.handleChange} />
                <Typography variant="subtitle2">Wiadomość E-mail</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                <Checkbox id="sms_message" name="sms_message" checked={formik.values.sms_message} onChange={formik.handleChange} />
                <Typography variant="subtitle2">Wiadomość SMS</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                <Checkbox id="sms_message_any_number" name="sms_message_any_number" checked={formik.values.sms_message_any_number} onChange={formik.handleChange} />
                <Typography variant="subtitle2">Wiadomość SMS do dowolnych numerów</Typography>
              </Stack>
            </Stack>
            <Box height={20} />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setIdList(rows.map(row => row.id))}>zaznacz wszystko</Button>
              <Button color="error" onClick={() => setIdList([])}>odznacz wszystko</Button>
            </Stack>
            <Box height={20} />
            <FormControl>
              <InputLabel>Wyślij do</InputLabel>
              <Select
                multiple
                value={idList}
                renderValue={selected => {
                  const names = [];
                  selected.forEach(id => {
                    const found = rows.find(element => element.id === id);
                    if (!_.isNil(found)) {
                      names.push(`${found.name} ${found.surname}`);
                    }
                  });

                  return names.join(', ');
                }}
                onChange={handleChange}
                input={<OutlinedInput label="Wyślij do" />}
              >
                {rows.map(row => (
                  <MenuItem key={row.id} value={row.id}>
                    <ListItemAvatar>
                      <Avatar src={_.isNil(row.photo) ? defaultImage : row.photo} />
                    </ListItemAvatar>
                    <ListItemText primary={`${row.name} ${row.surname}`} secondary={`${row.correspondence_email}, ${row.phone}`} />
                    <Checkbox checked={idList.indexOf(row.id) > -1} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formik.values.sms_message_any_number
              ? <>
                <Box height={20} />
                <TextField
                  id="any_numbers"
                  name="any_numbers"
                  multiline
                  rows={3}
                  label="Dowolne numery"
                  value={formik.values.any_numbers}
                  onChange={formik.handleChange}
                  error={formik.errors.any_numbers && formik.touched.any_numbers}
                  helperText={formik.errors.any_numbers ? formik.errors.any_numbers : 'Każdy numer od nowej linii'}
                />
              </>
              : null}
            {formik.values.email_message
              ? <>
                <Box height={20} />
                <TextField
                  id="subject"
                  name="subject"
                  label="Temat"
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  error={formik.errors.subject && formik.touched.subject}
                  helperText={formik.errors.subject}
                />
              </>
              : null}
            <Box height={20} />
            <TextField
              id="message"
              name="message"
              multiline
              rows={8}
              label="Treść wiadomości"
              value={formik.values.message}
              onChange={formik.handleChange}
              error={formik.errors.message && formik.touched.message}
              helperText={formik.errors.message}
              disabled={!formik.values.email_message && !formik.values.sms_message && !formik.values.sms_message_any_number}
            />
            {error
              ? <>
                <Box height={40} />
                <Alert variant="outlined" severity="error">Nie udało się wysłać wiadomości</Alert>
              </>
              : null}
            <Box height={40} />
            {loading
              ? <>
                <LinearProgress color="success" />
              </>
              : <Button disabled={!formik.values.email_message && !formik.values.sms_message && !formik.values.sms_message_any_number} type="submit" variant="contained">
                Wyślij
              </Button>}
          </form>
        </CardContent>
      </Card>
    </Modal>
  );
}
